import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Trans } from 'react-i18next';

import { Icon } from '@shared/ui/components/Icon';
import { cn } from '@shared/utils/helpers';

import { useDraggingOverWindow } from './useDraggingOverWindow';

export type FileInputProps = {
  id?: string;
  name?: string;
  value?: File[] | null;
  onChange?: (value: File[] | null) => void;
};

export function FileInput({ id, name, value, onChange }: FileInputProps) {
  const files = value ?? [];

  const { draggingOverWindow, stopDraggingOverWindow } = useDraggingOverWindow();
  const [draggingOverDropzone, setDraggingOverDropzone] = useState(false);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'image/bmp': ['.bmp'],
      'image/gif': ['.gif'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
    },
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  });

  function handleDragEnter() {
    setDraggingOverDropzone(true);
  }

  function handleDragLeave() {
    setDraggingOverDropzone(false);
  }

  function handleDrop(droppedFiles: File[]) {
    stopDraggingOverWindow();
    setDraggingOverDropzone(false);

    onChange?.([...files, ...droppedFiles]);
  }

  function handleRemove(index: number) {
    return function () {
      onChange?.(files.filter((_, i) => i !== index));
    };
  }

  return (
    <div className="grid gap-3">
      <ul className="contents">
        {files.map((file, index) => (
          <li key={index} className="flex items-center gap-6 group">
            {['image/jpeg', 'image/png'].includes(file.type) && (
              <img src={URL.createObjectURL(file)} className="w-24 h-16 rounded object-cover" alt={file.name} />
            )}
            {['application/pdf'].includes(file.type) && (
              <div className="w-24 h-16 rounded bg-indigo-50 border border-indigo-100 text-indigo-500 text-xl flex items-center justify-center">
                <Icon icon="file-pdf" />
              </div>
            )}
            <p className="flex-1 font-normal">{file.name}</p>
            <button
              type="button"
              className={cn(
                'w-6 h-6 text-sm flex items-center justify-center rounded-full text-gray-400',
                'hover:text-red-500 group-hover:bg-gray-100'
              )}
              onClick={handleRemove(index)}
            >
              <Icon icon="close" />
            </button>
          </li>
        ))}
      </ul>
      <div className="contents" {...getRootProps()}>
        <input id={id} name={name} {...getInputProps()} />
        <div
          className={cn(
            'py-6 rounded bg-gray-50 border border-dashed cursor-default flex flex-col items-center justify-center gap-0.5',
            {
              'bg-indigo-50 border-indigo-200': draggingOverWindow,
              'bg-green-50 border-green-200': draggingOverDropzone,
            }
          )}
        >
          <p className="font-medium">
            {draggingOverWindow ? <Trans>Drop here</Trans> : <Trans>Select or drop files…</Trans>}
          </p>
          <p className="text-sm text-gray-600">.bmp .jpeg .gif .pdf .png</p>
        </div>
      </div>
    </div>
  );
}
